import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrophy,
  faStar,
  faCrown,
  faMedal,
  faGraduationCap,
  faAward,
  faHeart,
  faGem,
  faRocket,
  faLightbulb,
  faShieldAlt,
  faBullseye,
  faUser,
  faCheck,
  faArrowLeft
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ApiServices from "../Services/ApiServices";
import { toastHelper } from '../../Helpers/toastHelper';
import Loader from "../Shared/Loader";
import Header from "../Shared/Header";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const motivationalMessages = [
  { quote: "النجاح ليس نهاية، والفشل ليس قاتلاً: ما يهم هو الشجاعة للاستمرار", subtitle: "استمر في التعلم والنمو، فكل خطوة تقربك من أهدافك" },
  { quote: "المعرفة قوة، والتعلم المستمر هو سر النجاح", subtitle: "لا تتوقف عن التعلم أبداً، فالعالم يتغير كل يوم" },
  { quote: "التفوق ليس صدفة، بل نتيجة العمل الجاد والتفاني", subtitle: "اجتهد اليوم لتحصد النجاح غداً" },
  { quote: "كل إنجاز عظيم يبدأ بقرار صغير", subtitle: "ابدأ من حيث أنت واستخدم ما لديك" },
  { quote: "التعلم رحلة لا تنتهي، استمتع بكل خطوة فيها", subtitle: "كل معلومة جديدة تفتح لك باباً جديداً" },
  { quote: "التميز ليس مهارة، بل عادة تكررها يومياً", subtitle: "اجعل من التعلم جزءاً من روتينك اليومي" },
  { quote: "لا تخف من الأخطاء، فهي خطواتك نحو التعلم", subtitle: "كل خطأ يعلمك شيئاً جديداً" },
  { quote: "النجاح مجموع الجهود الصغيرة المتكررة يومياً", subtitle: "ثابر يومياً وستصل إلى قمة النجاح" },
  { quote: "النجاح في الامتحان يبدأ بالنجاح في الاستعداد", subtitle: "جهز نفسك جيداً وستحصد النتائج المتميزة" }
];

const HonorBoardExams = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [randomMessage, setRandomMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * motivationalMessages.length);
    setRandomMessage(motivationalMessages[randomIndex]);
  }, []);

  useEffect(() => {
    const fetchHonorBoard = async () => {
      try {
        const response = await ApiServices.get("HonorBoardExams");
        if (response && Array.isArray(response)) {
          const sortedStudents = response.sort((a, b) => b.overall_percentage - a.overall_percentage);
          setStudents(sortedStudents);
          setLoading(false);
          setTimeout(() => {
            setShowConfetti(true);
            setTimeout(() => setShowConfetti(false), 5000);
          }, 500);
        } else {
          setLoading(false);
          toastHelper.error("حدث خطأ أثناء تحميل لوحة الشرف");
        }
      } catch (err) {
        setLoading(false);
        toastHelper.error("حدث خطأ أثناء تحميل لوحة الشرف");
      }
    };
    fetchHonorBoard();
  }, []);

  const getRankIcon = (index) => {
    switch (index) {
      case 0: return <FontAwesomeIcon icon={faCrown} className="hb-rank-gold" />;
      case 1: return <FontAwesomeIcon icon={faTrophy} className="hb-rank-silver" />;
      case 2: return <FontAwesomeIcon icon={faMedal} className="hb-rank-bronze" />;
      default: return <FontAwesomeIcon icon={faStar} className="hb-rank-star" />;
    }
  };

  const getAchievementIcon = (p) => {
    if (p >= 95) return <FontAwesomeIcon icon={faGem} />;
    if (p >= 90) return <FontAwesomeIcon icon={faRocket} />;
    if (p >= 85) return <FontAwesomeIcon icon={faLightbulb} />;
    if (p >= 80) return <FontAwesomeIcon icon={faShieldAlt} />;
    return <FontAwesomeIcon icon={faBullseye} />;
  };

  if (loading) return <div className="hb-root"><Header /><div className="hb-loader"><Loader /></div></div>;

  return (
    <div className="hb-root" dir="rtl">
      <Header />

      {/* ── Festivals Animations (Retained as requested) ── */}
      <div className="hb-festivals">
        <div className="hb-balloons">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="hb-balloon" style={{ left: `${8 + i * 8}%`, animationDelay: `${i * 0.4}s`, animationDuration: `${6 + Math.random() * 4}s` }}>
              <div className="hb-balloon-body" style={{ background: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFD700', '#96CEB4', '#FF9FF3', '#54A0FF', '#5F27CD'][i % 8] }} />
              <div className="hb-balloon-string" />
            </div>
          ))}
        </div>
        <div className="hb-hearts">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="hb-heart" style={{ left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 5}s`, animationDuration: `${5 + Math.random() * 5}s` }}>❤️</div>
          ))}
        </div>
        <div className="hb-stars">
          {[...Array(30)].map((_, i) => (
            <div key={i} className="hb-star-twinkle" style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 3}s` }}>⭐</div>
          ))}
        </div>
        <AnimatePresence>
          {showConfetti && (
            <div className="hb-confetti-wrap">
              {[...Array(60)].map((_, i) => (
                <div key={i} className="hb-confetti" style={{ left: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 2}s`, background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1'][i % 4] }} />
              ))}
            </div>
          )}
        </AnimatePresence>
      </div>

      <section className="hb-content">
        <div className="hb-container">

          {/* Header */}
          <motion.div className="hb-header" initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <motion.div className="hb-eyebrow" variants={fadeUp} custom={0}>
              <span className="hb-eyebrow-dot" />
              المتفوقون والمتميزون
            </motion.div>
            <motion.h2 className="hb-title" variants={fadeUp} custom={1}>
              لوحة <em>شرف امتحاني</em>
            </motion.h2>
            <motion.p className="hb-subtitle" variants={fadeUp} custom={2}>
              نحتفي بأبطالنا الذين حققوا أعلى النتائج في الاختبارات. كل اسم هنا هو قصة نجاح وإلهام للجميع.
            </motion.p>
          </motion.div>

          {/* Podium (Top 3) */}
          {students.length >= 3 && (
            <motion.div
              className="hb-podium"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {/* Rank 2 */}
              <motion.div className="hb-podium-item is-silver" variants={fadeUp} custom={4}>
                <div className="hb-podium-avatar">
                  <FontAwesomeIcon icon={faGraduationCap} />
                </div>
                <h3 className="hb-podium-name">{students[1].full_name}</h3>
                <div className="hb-podium-score">%{students[1].overall_percentage}</div>
                <div className="hb-podium-base">2</div>
                {getRankIcon(1)}
              </motion.div>

              {/* Rank 1 */}
              <motion.div className="hb-podium-item is-gold" variants={fadeUp} custom={3}>
                <div className="hb-podium-avatar winner">
                  <FontAwesomeIcon icon={faCrown} />
                </div>
                <h3 className="hb-podium-name">{students[0].full_name}</h3>
                <div className="hb-podium-score">%{students[0].overall_percentage}</div>
                <div className="hb-podium-base">1</div>
                {getRankIcon(0)}
                <div className="hb-winner-glow" />
              </motion.div>

              {/* Rank 3 */}
              <motion.div className="hb-podium-item is-bronze" variants={fadeUp} custom={5}>
                <div className="hb-podium-avatar">
                  <FontAwesomeIcon icon={faMedal} />
                </div>
                <h3 className="hb-podium-name">{students[2].full_name}</h3>
                <div className="hb-podium-score">%{students[2].overall_percentage}</div>
                <div className="hb-podium-base">3</div>
                {getRankIcon(2)}
              </motion.div>
            </motion.div>
          )}

          {/* Honor List */}
          <div className="hb-list-section">
            <motion.h3 className="hb-section-title" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              قائمة الشرف <em>الكاملة</em>
            </motion.h3>

            <div className="hb-grid">
              {students.map((s, i) => (
                <motion.div
                  key={s.id}
                  className="hb-card"
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  custom={i % 6}
                  whileHover={{ y: -5 }}
                >
                  <div className="hb-card-rank">
                    {getRankIcon(i)}
                    <span className="hb-rank-num">#{i + 1}</span>
                  </div>

                  <div className="hb-card-info">
                    <div className="hb-card-avatar">
                      <FontAwesomeIcon icon={faGraduationCap} />
                    </div>
                    <div className="hb-card-text">
                      <h4 className="hb-student-name">{s.full_name}</h4>
                      <div className="hb-student-meta">
                        <span><FontAwesomeIcon icon={faUser} /> المعلم: {s.teacher_name}</span>
                        <span><FontAwesomeIcon icon={faCheck} /> الاختبارات: {s.exams_participated}</span>
                      </div>
                    </div>
                  </div>

                  <div className="hb-card-score">
                    <div className="hb-score-circle" style={{ '--score-p': `${s.overall_percentage}%` }}>
                      <span className="hb-score-val">%{s.overall_percentage}</span>
                    </div>
                    <div className="hb-achievement-tag">
                      {getAchievementIcon(s.overall_percentage)}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Motivation */}
          {randomMessage && (
            <motion.div
              className="hb-motivation"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="hb-motivation-inner">
                <FontAwesomeIcon icon={faHeart} className="hb-m-icon" />
                <h3 className="hb-m-title">رسالة تشجيعية</h3>
                <p className="hb-m-quote">"{randomMessage.quote}"</p>
                <p className="hb-m-sub">{randomMessage.subtitle}</p>
              </div>
            </motion.div>
          )}

        </div>
      </section>

      <style jsx>{`
        .hb-root {
          font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
          position: relative;
          overflow-x: hidden;
        }

        .hb-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .hb-content { position: relative; z-index: 10; padding: 140px 0 120px; }

        /* ── Header ── */
        .hb-header { text-align: center; margin-bottom: 72px; }
        .hb-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .hb-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: hb-pulse 2.2s ease-in-out infinite;
        }
        @keyframes hb-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .hb-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: #0f172a; margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .hb-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hb-subtitle {
          font-size: 1.25rem; color: #64748b; line-height: 1.85; margin: 0 auto;
          max-width: 700px; font-weight: 500;
        }

        /* ── Podium ── */
        .hb-podium {
          display: flex; align-items: flex-end; justify-content: center; gap: 20px; margin-bottom: 100px;
          padding-top: 40px;
        }
        .hb-podium-item {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
          border-radius: 32px; padding: 40px 24px; text-align: center;
          position: relative; width: 280px; transition: 0.3s;
        }
        .hb-podium-item.is-gold {
          height: 380px; z-index: 5; border-color: #6366f1; border-width: 2px;
          box-shadow: 0 20px 50px rgba(99, 102, 241, 0.08);
        }
        .hb-podium-item.is-silver { height: 320px; z-index: 4; }
        .hb-podium-item.is-bronze { height: 280px; z-index: 3; }

        .hb-podium-avatar {
          width: 80px; height: 80px; background: #f8fafc; color: #64748b;
          border-radius: 50%; display: flex; align-items: center; justify-content: center;
          font-size: 2rem; margin: 0 auto 20px; position: relative; z-index: 2;
        }
        .hb-podium-avatar.winner { background: #fefce8; color: #6366f1; width: 100px; height: 100px; font-size: 2.8rem; margin-top: -20px; }
        
        .hb-podium-name { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin: 0 0 8px; }
        .hb-podium-score { font-size: 2rem; font-weight: 900; color: #6366f1; }
        .hb-podium-base {
          position: absolute; bottom: 0; left: 0; right: 0; height: 60px;
          background: #f8fafc; border-top: 1.5px solid #f1f5f9; border-radius: 0 0 32px 32px;
          display: flex; align-items: center; justify-content: center;
          font-size: 2rem; font-weight: 900; color: #e2e8f0;
        }

        .hb-rank-gold { position: absolute; top: -15px; left: 50%; transform: translateX(-50%); font-size: 2rem; color: #6366f1; }
        .hb-rank-silver { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); font-size: 1.8rem; color: #94a3b8; }
        .hb-rank-bronze { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); font-size: 1.6rem; color: #b45309; }
        .hb-winner-glow {
          position: absolute; inset: 0; border-radius: 32px;
          background: radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%);
          pointer-events: none;
        }

        /* ── Grid ── */
        .hb-section-title { font-size: 2.2rem; font-weight: 900; color: #0f172a; text-align: center; margin-bottom: 48px; }
        .hb-section-title em { font-style: normal; color: #6366f1; }
        .hb-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 24px; }
        .hb-card {
          background: #fff; border: 1.5px solid rgba(15,23,42,0.06); border-radius: 24px;
          padding: 24px; display: flex; align-items: center; gap: 20px; transition: 0.3s;
          position: relative; overflow: hidden;
        }
        .hb-card:hover { border-color: rgba(15,23,42,0.12); box-shadow: 0 12px 32px rgba(0,0,0,0.03); }

        .hb-card-rank { display: flex; flex-direction: column; align-items: center; gap: 4px; min-width: 40px; }
        .hb-rank-star { color: #cbd5e1; }
        .hb-rank-num { font-weight: 900; color: #94a3b8; font-size: 0.9rem; }

        .hb-card-info { flex: 1; display: flex; align-items: center; gap: 16px; }
        .hb-card-avatar { width: 56px; height: 56px; background: #f1f5f9; color: #64748b; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 1.4rem; }
        .hb-student-name { font-size: 1.25rem; font-weight: 900; color: #0f172a; margin: 0 0 6px; }
        .hb-student-meta { display: flex; flex-direction: column; gap: 2px; color: #94a3b8; font-weight: 700; font-size: 0.85rem; }
        .hb-student-meta svg { color: #cbd5e1; margin-left: 4px; }

        .hb-card-score { display: flex; flex-direction: column; align-items: center; gap: 8px; }
        .hb-score-circle {
          width: 52px; height: 52px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          position: relative; background: #f8fafc;
        }
        .hb-score-circle::after {
          content: ''; position: absolute; inset: -3px; border-radius: 50%;
          background: conic-gradient(#6366f1 var(--score-p), transparent 0);
          z-index: -1;
        }
        .hb-score-val { font-size: 0.95rem; font-weight: 900; color: #0f172a; }
        .hb-achievement-tag { font-size: 1.1rem; color: #6366f1; }

        /* ── Motivation ── */
        .hb-motivation { margin-top: 100px; text-align: center; }
        .hb-motivation-inner {
          background: #f8fafc; border-radius: 40px; padding: 60px 40px;
          border: 1.5px solid #f1f5f9; position: relative; overflow: hidden;
        }
        .hb-m-icon { font-size: 3rem; color: #ef4444; opacity: 0.1; position: absolute; top: 20px; right: 20px; }
        .hb-m-title { font-size: 1.5rem; font-weight: 900; color: #6366f1; margin-bottom: 20px; }
        .hb-m-quote { font-size: 1.4rem; font-weight: 800; color: #0f172a; line-height: 1.7; margin-bottom: 12px; }
        .hb-m-sub { font-size: 1.05rem; color: #64748b; font-weight: 600; }

        /* ── FESTIVALS (Retained) ── */
        .hb-festivals { position: fixed; inset: 0; z-index: 1; pointer-events: none; }
        .hb-balloon { position: absolute; bottom: -100px; animation: hb-rise linear infinite; }
        .hb-balloon-body { width: 50px; height: 60px; border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%; box-shadow: inset -10px -10px 0 rgba(0,0,0,0.05); }
        .hb-balloon-string { width: 2px; height: 60px; background: rgba(0,0,0,0.1); margin: 0 auto; }
        @keyframes hb-rise { 0%{ transform: translateY(0); opacity: 1; } 100%{ transform: translateY(-120vh); opacity: 0; } }
        
        .hb-heart { position: absolute; bottom: -50px; font-size: 1.5rem; animation: hb-heart-rise linear infinite; }
        @keyframes hb-heart-rise { 0%{ transform: translateY(0) rotate(0deg); opacity: 1; } 100%{ transform: translateY(-110vh) rotate(360deg); opacity: 0; } }
        
        .hb-star-twinkle { position: absolute; font-size: 1rem; animation: hb-twinkle 3s ease-in-out infinite; }
        @keyframes hb-twinkle { 0%,100%{ opacity: 0.2; transform: scale(1); } 50%{ opacity: 1; transform: scale(1.3); } }
        
        .hb-confetti-wrap { position: fixed; inset: 0; z-index: 50; }
        .hb-confetti { position: absolute; top: -20px; width: 10px; height: 10px; border-radius: 2px; animation: hb-fall 3s linear forwards; }
        @keyframes hb-fall { 0%{ transform: translateY(0) rotate(0deg); } 100%{ transform: translateY(110vh) rotate(720deg); } }

        .hb-loader { height: 70vh; display: flex; align-items: center; justify-content: center; }

        @media (max-width: 1024px) {
          .hb-podium { flex-direction: column; align-items: center; gap: 40px; }
          .hb-podium-item.is-gold { order: -1; }
        }

        @media (max-width: 768px) {
          .hb-content { padding-top: 140px; }
          .hb-title { font-size: 2.2rem; }
          .hb-grid { grid-template-columns: 1fr; }
          .hb-podium-item { width: 100%; max-width: 340px; }
        }
      `}</style>
    </div>
  );
};

export default HonorBoardExams;